/**
 * TourGuide
 * Interactive guided tour using driver.js with optional audio narration.
 * Highlights UI elements sequentially with spotlight overlay and popover explanations.
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import { driver, type DriveStep, type Config } from 'driver.js';
import 'driver.js/dist/driver.css';
import { type TourDefinition, markTourSeen, hasTourBeenSeen } from './tourSteps';
import './TourGuide.css';

interface TourGuideProps {
  tour: TourDefinition;
  /** Called when the tour finishes or is dismissed */
  onComplete?: () => void;
  /** Audio source URLs keyed by step index (optional — tour works without audio) */
  audioSources?: Record<number, string>;
}

export function useTourGuide({ tour, onComplete, audioSources }: TourGuideProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const isActiveRef = useRef(false);
  const reachedEndRef = useRef(false);
  const [hasBeenSeen, setHasBeenSeen] = useState(() => hasTourBeenSeen(tour.id));

  // Cleanup audio and driver on unmount.
  // Nullify driverRef BEFORE calling destroy to prevent re-entrant cycle
  // (onDestroyStarted also calls driverRef.current?.destroy()).
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (driverRef.current) {
        const d = driverRef.current;
        driverRef.current = null;
        d.destroy();
      }
    };
  }, []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const playAudio = useCallback((stepIndex: number) => {
    stopAudio();
    const src = audioSources?.[stepIndex];
    if (!src) return;

    const audio = new Audio(src);
    audioRef.current = audio;
    audio.play().catch((error: DOMException) => {
      if (error.name === 'NotAllowedError') {
        // Browser autoplay policy — tour continues without audio
        console.warn('[TourGuide] Audio autoplay blocked by browser policy');
      } else {
        console.error('[TourGuide] Audio playback failed for step', stepIndex, error);
      }
    });
  }, [audioSources, stopAudio]);

  const startTour = useCallback(() => {
    if (isActiveRef.current) return;
    isActiveRef.current = true;
    reachedEndRef.current = false;

    // Filter steps to only those whose target element exists in the DOM.
    // This prevents orphaned popovers when conditional elements are not rendered.
    const visibleSteps: DriveStep[] = tour.steps
      .filter((step) => document.querySelector(step.element) !== null)
      .map((step) => ({
        element: step.element,
        popover: {
          title: step.title,
          description: buildStepContent(step.description, step.transcript),
          side: step.side ?? 'bottom',
          popoverClass: 'tour-guide-popover',
        },
      }));

    if (visibleSteps.length === 0) {
      isActiveRef.current = false;
      console.warn(`[TourGuide] Tour "${tour.id}" skipped — none of the ${tour.steps.length} target elements found in DOM`);
      return;
    }

    const config: Config = {
      steps: visibleSteps,
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      stagePadding: 8,
      stageRadius: 8,
      popoverOffset: 12,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      onNextClick: () => {
        stopAudio();
        driverRef.current?.moveNext();
      },
      onPrevClick: () => {
        stopAudio();
        driverRef.current?.movePrevious();
      },
      onHighlightStarted: (_element, _step, { state }) => {
        const idx = state.activeIndex ?? 0;
        // Track if user reached the final step (fires on highlight start,
        // so a user who sees the last step and then closes is counted as complete)
        if (idx === visibleSteps.length - 1) {
          reachedEndRef.current = true;
        }
        if (audioSources?.[idx]) {
          playAudio(idx);
        }
      },
      onDestroyStarted: () => {
        stopAudio();
        driverRef.current?.destroy();
        isActiveRef.current = false;
        // Only mark as seen if user completed the tour (reached final step)
        if (reachedEndRef.current) {
          markTourSeen(tour.id);
          setHasBeenSeen(true);
        }
        onComplete?.();
      },
    };

    try {
      const d = driver(config);
      driverRef.current = d;
      d.drive();
    } catch (error) {
      isActiveRef.current = false;
      console.error('[TourGuide] Failed to start tour:', error);
    }
  }, [tour, onComplete, audioSources, playAudio, stopAudio]);

  return { startTour, hasBeenSeen };
}

/** Escape HTML special characters to prevent XSS.
 *  Tour content is currently hardcoded in tourSteps.ts, but this guard
 *  protects against future callers that might pass dynamic content. */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function buildStepContent(description: string, transcript: string): string {
  return `
    ${escapeHtml(description)}
    <details class="tour-step-transcript">
      <summary>Why this matters</summary>
      <p>${escapeHtml(transcript)}</p>
    </details>
  `;
}
